import { Box, Typography } from '@mui/material';
import { formatTranslation } from 'intl';
import { AnalyticsTranslationKeys } from 'intl/keys';
import React, { ReactElement } from 'react';
import { useIntl } from 'react-intl';
import ChartNoDataIcon from '../../assets/icons/chart_no_data.svg?react';
import { CHART_FONT_FAMILY, getChartAreaSx, getDashboardChartAreaSx } from '../theme/muiChartSlotProps';

type EmptyChartProps = Readonly<{
  variant?: 'dashboard' | 'details';
}>;

function EmptyChart({ variant = 'dashboard' }: EmptyChartProps): ReactElement {
  const intl = useIntl();
  const isDashboard = variant === 'dashboard';

  return (
    <Box
      sx={{
        ...(isDashboard
          ? { ...getDashboardChartAreaSx(), alignItems: 'center' }
          : getChartAreaSx()),
        width: '100%',
        ...(!isDashboard
          ? { flex: 1, minHeight: 0 }
          : {}),
        flexDirection: 'column',
        textAlign: 'center',
        gap: isDashboard ? 1.25 : 1.5,
        py: isDashboard ? 1 : 2,
        px: 2,
      }}
    >
      <Box
        sx={{
          width: isDashboard ? 88 : 96,
          height: isDashboard ? 88 : 96,
          borderRadius: '50%',
          bgcolor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Box
          component={ChartNoDataIcon}
          sx={{
            width: isDashboard ? 40 : 44,
            height: isDashboard ? 40 : 44,
            opacity: 0.55,
          }}
        />
      </Box>

      <Typography
        variant={isDashboard ? 'body2' : 'subtitle1'}
        sx={{
          fontFamily: CHART_FONT_FAMILY,
          fontWeight: 600,
          color: 'text.primary',
          lineHeight: 1.3,
        }}
      >
        {formatTranslation(intl, AnalyticsTranslationKeys.noData)}
      </Typography>

      <Box sx={{ maxWidth: isDashboard ? 200 : 260 }}>
        <Typography
          variant='caption'
          sx={{
            fontFamily: CHART_FONT_FAMILY,
            color: 'text.secondary',
            lineHeight: 1.45,
            display: 'block',
          }}
        >
          {formatTranslation(intl, AnalyticsTranslationKeys.noDataHelperLine1)}
        </Typography>
        <Typography
          variant='caption'
          sx={{
            fontFamily: CHART_FONT_FAMILY,
            color: 'text.secondary',
            lineHeight: 1.45,
            display: 'block',
            mt: 0.25,
          }}
        >
          {formatTranslation(intl, AnalyticsTranslationKeys.noDataHelperLine2)}
        </Typography>
      </Box>
    </Box>
  );
}

export const MemoEmptyChart = React.memo(EmptyChart);
